import MainBlock from "components/DesignSystem/LayoutBlocks/MainBlock";
import React, { useEffect, useCallback, useContext, useReducer } from "react";
import { useAuth } from "hooks/Auth";
import { useParams } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { apiGet, apiPost, apiPut, messageFromException } from "helpers/Request";
import {
    transform as contentTransformer,
    ContentObjectSchema,
    prepare as contentPreparer,
} from "schemas/Content";
import EditForm from "./EditForm";
import CachedBanner from "Screens/Article/CachedBanner";
import { useDispatch, useSelector } from "react-redux";
import { addItem as addRecentItem } from "redux/actions/recent-items";
import { useHistory } from "react-router-dom";
import { transform } from "lodash";
import { ContentContext } from "context/Content";
import { AppContext } from "context/App";
import { toast } from "react-hot-toast";
import { modal } from "components/HeadlessModal";

// ensures the content formValue shape matches the form schema
const ensureContentShape = (content, contentFlow) => {
    if (content.hasOwnProperty("revived_from_cache")) {
        return content;
    }
    const allProps = contentFlow.metaProperties["content"].concat(
        contentFlow.metaProperties["content-lower"]
    );

    return Object.assign({}, content, {
        meta: transform(
            allProps,
            (result, metaProp, key) => {
                const val = content.metaProperties.find(
                    (mp) => mp.field === metaProp.field
                );
                result[metaProp.field] = val ? val.value : "";
            },
            {}
        ),
        tags: content.tags.map((tag) => {
            return { label: tag.name, value: tag.uuid };
        }),
        authors: content.authors.map((author) => {
            return { label: author.name, value: author.uuid };
        }),
        workflow: content.workflow
            ? {
                  value: content.workflow.id,
                  label: content.workflow.name,
              }
            : {
                  value: contentFlow.workflows[0].id,
                  label: contentFlow.workflows[0].name,
              },
        type: "media",
    });
};

const prepareForSave = (values, content, contentFlow) => {
    const allProps = contentFlow.metaProperties["content"].concat(
        contentFlow.metaProperties["content-lower"]
    );

    const tags = values.tags.map((tag) => tag.value);
    const authors = values.authors.map((author) => author.value);

    const meta = Object.keys(values.meta)
        .map((key, i) => {
            const type = allProps.find((mp) => mp.field === key);
            return {
                field: key,
                value: values.meta[key],
                type: type ? type.type : "NoopMetaProperty",
            };
        })
        .filter((mp) => mp.value !== "");

    return contentPreparer(
        Object.assign({}, content, values, {
            tags: tags,
            authors: authors,
            meta: meta,
            dominant_attachment: null,
            workflow_id: values.workflow.value,
            layout_template:
                values.layout_template === "Default"
                    ? null
                    : values.layout_template,
        })
    );
};

const showSaveErrorModal = (err) => {
    modal.error({
        title: "Unable to save media",
        children: messageFromException(err),
    });
};

const showLoadErrorModal = (history, err) => {
    modal.error({
        title: "Unable to load media",
        children: messageFromException(err),
        confirmText: "Try again",
        onRequestConfirm: (next) => {
            history.push("/app/dashboard");
            next();
        },
    });
};

const reducer = (oldState, newState) => {
    return Object.assign({}, oldState, newState);
};

const loadContent = async (uuid, pub, setState) => {
    return apiGet("/v4/content/" + uuid, pub, {
        include: "container",
    })
        .then((response) => {
            const item = contentTransformer(response);

            // forward the media placeholders for galleries to the actual gallery page
            if (item.container && item.container.type === "gallery") {
                throw new Error("Item is placeholder", { cause: item });
            }
            return item;
        })
        .then((item) => {
            setState({
                content: item,
                loading: false,
            });
            return item;
        });
};

export default function MediaPage(props) {
    const { uuid } = useParams();
    const location = useLocation();
    const auth = useAuth();

    const dispatch = useDispatch();
    const history = useHistory();

    const [state, setState] = useReducer(reducer, {
        content: ContentObjectSchema,
        formValues: ContentObjectSchema,
        loading: false,
        saving: false,
        isUserEditable: false,
        performingStateUpdate: false,
        requestReload: useCallback(() => {
            loadContent(uuid, auth.publication, setState).catch((err) => {
                showLoadErrorModal(history, err);
            });
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [uuid, auth.publication]),
    });

    const { setCurrentNav } = useContext(AppContext);

    const contentFlow = useSelector((state) => state.contentFlow);

    // const kvStore = useMemo(() => {
    //     return new IdbKvStore("contentCache");
    // }, []);

    // const handleRequestReload = () => {
    //     setState((state) => state + 1);
    // };

    const onSubmit = (values) => {
        if (
            state.content.lock &&
            state.content.lock.user.uuid !== auth.user.uuid
        ) {
            console.log("not locked by user");
            return;
        } else if (state.content.uuid && !state.content.lock) {
            console.log("no content lock");
            return;
        }

        const data = prepareForSave(values, state.content, contentFlow);

        setState({ saving: true });
        const toastId = toast.loading("Saving...");

        return apiPut(
            "/v4/content/" + state.content.uuid,
            auth.publication,
            data
        )
            .then((resp) => {
                const item = contentTransformer(resp);
                toast.success("Saved!", { id: toastId });

                if (!state.content.uuid) {
                    // was a new item, so redirect
                    apiPost("/v4/lock-request", auth.publication, {
                        srn: item.srn,
                    })
                        .then((resp) => {
                            history.push(item.edit_url);
                        })
                        .catch((err) => {
                            toast.show("Unable to acquire lock");
                        });
                }

                setState({ content: item, saving: false, loading: false });
                // return releaseLockOnSave;
            })
            .catch((err) => {
                setState({ saving: false });
                toast.dismiss(toastId);
                showSaveErrorModal(err);
            });
    };

    useEffect(() => {
        if (!uuid || uuid === "new") {
            setState({
                loading: false,
                content: ContentObjectSchema,
            });
            return;
        }

        loadContent(uuid, auth.publication, setState)
            .then((item) => {
                dispatch(addRecentItem(item));
            })
            .catch((err) => {
                if (err.message === "Item is placeholder") {
                    history.replace(`/app/gallery/${err.cause.container.uuid}`);
                    return;
                }
                showLoadErrorModal(history, err);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location, uuid, auth.publication, dispatch]);

    useEffect(() => {
        setCurrentNav("content");
    }, [setCurrentNav]);

    useEffect(() => {
        // fixes a bug where the console notes a component updating another, unmounted
        // component. just delay the update until the next tick
        setTimeout(() => {
            setState({
                formValues: ensureContentShape(state.content, contentFlow),
            });
        });
    }, [state.content, contentFlow]);

    useEffect(() => {
        if (
            state.content.uuid &&
            state.content.lock &&
            state.content.lock.user.uuid === auth.user.uuid
        ) {
            setState({ isUserEditable: true });
        } else if (!state.content.uuid) {
            setState({ isUserEditable: true });
        } else {
            setState({ isUserEditable: false });
        }
    }, [state.content, auth.user]);

    // content and form values are provided via context provider
    return (
        <ContentContext.Provider
            value={{
                state,
                setState,
            }}
        >
            <MainBlock className="z-0">
                <CachedBanner />
                <EditForm onSubmit={onSubmit} />
            </MainBlock>
        </ContentContext.Provider>
    );
}
